import React, { useEffect, useMemo, useState } from "react";
import { useStoreActions } from "easy-peasy";
import MDBox from "components/MDBox";
import CollapsibleCard from "components/CollapsibleCard";
import OcrolusPivotTable from "./OcrolusPivotTable";
import { formatCurrencyUSD } from "utils/CommonUtils";
import moment from "moment";
import Loader from "components/Loader/Basic";
import MDTypography from "components/MDTypography";

interface OcrolusRecurringPaymentsProps {
  dealApplicationId: any;
  recurringDebits: any[];
  loading: boolean;
}

const OcrolusRecurringPayments: React.FC<OcrolusRecurringPaymentsProps> = ({
  recurringDebits = [],
  loading,
}) => {
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [rows, setRows] = useState<any[]>([]);

  const setAdvancesData = useStoreActions(
    (actions: any) => actions.bankSummaryStore.setAdvancesData
  );

  useEffect(() => {
    if (recurringDebits.length === 0) {
      setRows([]);
      setSelectedRows(new Set());
      setAdvancesData([]);
      return;
    }

    const initializedRows = recurringDebits
      .filter((r) => Number(r?.monthlyAmount) > 0)
      .map((r, index) => ({
        id: `${r.funder || "unknown"}-${index}`,
        selected: true,
        ...r,
      }));

    setRows(initializedRows);

    const allIds = initializedRows.map((r) => r.id);
    setSelectedRows(new Set(allIds));

    setAdvancesData(initializedRows);
  }, [recurringDebits]);

  useEffect(() => {
    if (rows.length === 0) {
      setAdvancesData([]);
      return;
    }

    const selectedRowsArray = rows.filter((row) => selectedRows.has(row.id));

    setAdvancesData(selectedRowsArray);
  }, [selectedRows, rows]);

  const totalSelectedMonthlyAmount = useMemo(() => {
    return rows.reduce((total, row) => {
      if (selectedRows.has(row.id)) {
        return total + Number(row.monthlyAmount || 0);
      }
      return total;
    }, 0);
  }, [rows, selectedRows]);

  const handleSelectRow = (rowKey: string) => {
    setSelectedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(rowKey)) {
        newSet.delete(rowKey);
      } else {
        newSet.add(rowKey);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedRows.size === rows.length && rows.length > 0) {
      setSelectedRows(new Set());
    } else {
      const allIds = rows.map((r) => r.id);
      setSelectedRows(new Set(allIds));
    }
  };

  const columns = [
    {
      Header: "FUNDER",
      accessor: "funder",
      width: "220px",
    },
    {
      Header: "STATUS",
      accessor: "status",
      width: "80px",
    },
    {
      Header: "FREQUENCY",
      accessor: "frequency",
      width: "90px",
    },
    {
      Header: "DEBIT AMOUNT",
      accessor: "avgDebitAmount",
      width: "110px",
      Cell: ({ row: { original } }: any) => formatCurrencyUSD(original.avgDebitAmount),
    },
    {
      Header: "MONTHLY AMOUNT",
      accessor: "monthlyAmount",
      width: "130px",
      Cell: ({ row: { original } }: any) => formatCurrencyUSD(original.monthlyAmount),
    },
    {
      Header: "LAST PAYMENT",
      accessor: "lastPayment",
      width: "130px",
    },
    {
      Header: "NO. OF DEBITS",
      accessor: "numberOfDebits",
      width: "110px",
      align: "center" as const,
    },
  ];

  const renderTotal = (groupRows: any[], key: string) => {
    const total = groupRows.reduce((sum, r) => {
      return selectedRows.has(r.id) ? sum + Number(r[key] || 0) : sum;
    }, 0);

    return formatCurrencyUSD(total);
  };

  return (
    <CollapsibleCard
      header={
        <MDBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          width="100%"
          gap={2}
        >
          <MDTypography variant="h5" fontWeight="bold">
            Recurring Debits
          </MDTypography>

          <MDBox
            sx={{
              position: "relative",
              padding: "8px 18px 8px 14px",
              borderRadius: "12px",
              background:
                "linear-gradient(135deg, rgba(249, 115, 22, 0.12) 0%, rgba(234, 88, 12, 0.06) 100%)",
              border: "1px solid rgba(249, 115, 22, 0.25)",
              display: "flex",
              alignItems: "center",
              gap: "10px",
              "&::before": {
                content: '""',
                position: "absolute",
                left: "-1px",
                top: "50%",
                transform: "translateY(-50%)",
                width: "3px",
                height: "65%",
                backgroundColor: "#f97316",
                borderRadius: "0 3px 3px 0",
                opacity: 0.8,
              },
            }}
          >
            <MDTypography
              variant="caption"
              sx={{
                fontWeight: 600,
                color: "#9a3412",
                textTransform: "uppercase",
                letterSpacing: "0.6px",
                opacity: 0.85,
              }}
            >
              Total
            </MDTypography>

            <MDTypography
              variant="h6"
              fontWeight="bold"
              sx={{
                fontSize: "18px",
                color: "#ea580c",
                fontFeatureSettings: '"tnum"',
                letterSpacing: "-0.4px",
                lineHeight: 1,
              }}
            >
              {formatCurrencyUSD(totalSelectedMonthlyAmount)}
            </MDTypography>
          </MDBox>
        </MDBox>
      }
    >
      <MDBox paddingRight={3}>
        {loading ? (
          <Loader />
        ) : (
          <OcrolusPivotTable
            columns={columns}
            data={rows}
            rowKey="id"
            selectedRows={Array.from(selectedRows)}
            onSelectRow={handleSelectRow}
            onSelectAll={handleSelectAll}
            getRowStyle={() => 400}
            renderTotal={renderTotal}
            totalColumn={["monthlyAmount"]}
            groupBy="lastPayment"
            groupFormatter={(value: any) => {
              if (!value) return "Unknown";
              if (moment(value, moment.ISO_8601, true).isValid()) {
                return moment(value).format("MMMM YYYY");
              }
              return String(value);
            }}
            defaultExpanded={false}
          />
        )}
      </MDBox>
    </CollapsibleCard>
  );
};

export default OcrolusRecurringPayments;
